import React from 'react';
import { CalendarBlank, Users, VideoCamera, MapPin, User } from '@phosphor-icons/react';

// Analytics API row shape from learn_ilt_upcoming metric
interface ILTSessionRow {
    event_name: string;
    event_mode?: string;
    start_date?: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
    event_state?: string;
    trainer_name?: string;
    registered_learners?: number | string;
}

interface CalendarCardWidgetProps {
    data?: ILTSessionRow[] | null;
    loading?: boolean;
    error?: string;
}

const formatDateRange = (startDate?: string, endDate?: string): string => {
    if (!startDate) return '';
    const start = new Date(startDate);
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    if (!endDate || endDate === startDate) return startStr;

    const end = new Date(endDate);
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${startStr} – ${endStr}`;
};

const formatTimeRange = (startTime?: string, endTime?: string): string => {
    if (!startTime) return '';
    return endTime ? `${startTime} – ${endTime}` : startTime;
};

const extractDayNumber = (dateStr?: string): string => {
    if (!dateStr) return '—';
    return String(new Date(dateStr).getDate());
};

const extractMonthAbbr = (dateStr?: string): string => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
};

const getModeBadge = (mode?: string): { label: string; className: string } => {
    const m = (mode || '').toLowerCase();
    if (m === 'virtual' || m === 'online') {
        return { label: 'Virtual', className: 'bg-blue-100 text-blue-700' };
    }
    if (m === 'blended' || m === 'hybrid') {
        return { label: 'Blended', className: 'bg-violet-100 text-violet-700' };
    }
    // Default: in-person / classroom / face-to-face / etc.
    return { label: mode ? (mode.charAt(0).toUpperCase() + mode.slice(1).toLowerCase()) : 'In-Person', className: 'bg-emerald-100 text-emerald-700' };
};

const getStateBadge = (state?: string): { label: string; className: string } | null => {
    if (!state) return null;
    const s = state.toLowerCase();
    if (s === 'open' || s === 'active') return { label: 'Open', className: 'bg-emerald-100 text-emerald-700' };
    if (s === 'closed') return { label: 'Closed', className: 'bg-gray-100 text-gray-500' };
    if (s === 'cancelled' || s === 'canceled') return { label: 'Cancelled', className: 'bg-red-100 text-red-600' };
    if (s === 'full') return { label: 'Full', className: 'bg-amber-100 text-amber-700' };
    if (s === 'draft') return { label: 'Draft', className: 'bg-gray-100 text-gray-500' };
    return { label: state.charAt(0).toUpperCase() + state.slice(1).toLowerCase(), className: 'bg-gray-100 text-gray-500' };
};

const getModeIcon = (mode?: string) => {
    const m = (mode || '').toLowerCase();
    if (m === 'virtual' || m === 'online') return VideoCamera;
    if (m === 'blended' || m === 'hybrid') return MapPin;
    return Users;
};

export const CalendarCardWidget: React.FC<CalendarCardWidgetProps> = ({ data, loading, error }) => {
    const sessions = React.useMemo(() => {
        if (!Array.isArray(data)) return [];
        return [...data].sort((a, b) => {
            if (!a.start_date) return 1;
            if (!b.start_date) return -1;
            return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
        });
    }, [data]);

    return (
        <div className="flex flex-col h-full">
            <div className="px-4 pt-4 pb-2 flex items-center justify-between">
                {!loading && !error && (
                    <span className="text-xs text-gray-400">{sessions.length} session{sessions.length !== 1 ? 's' : ''}</span>
                )}
            </div>

            <div className="flex-1 overflow-auto px-4 pb-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : sessions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <CalendarBlank className="w-8 h-8" />
                        <span className="text-sm">No upcoming sessions</span>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {sessions.map((session, idx) => {
                            const ModeIcon = getModeIcon(session.event_mode);
                            const modeBadge = getModeBadge(session.event_mode);
                            const stateBadge = getStateBadge(session.event_state);
                            const dayNumber = extractDayNumber(session.start_date);
                            const monthAbbr = extractMonthAbbr(session.start_date);
                            const dateRangeLabel = formatDateRange(session.start_date, session.end_date);
                            const timeRangeLabel = formatTimeRange(session.start_time, session.end_time);
                            const rawCount = session.registered_learners !== undefined
                                ? Number(session.registered_learners)
                                : null;
                            const registeredCount = rawCount !== null && !isNaN(rawCount) ? rawCount : null;

                            return (
                                <div
                                    key={idx}
                                    className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                                        idx === 0
                                            ? 'bg-blue-50/50 border-blue-100'
                                            : 'bg-white border-gray-100 hover:bg-gray-50'
                                    }`}
                                >
                                    {/* Date block */}
                                    <div className="shrink-0 flex flex-col items-center justify-center w-10 h-10 rounded-lg bg-white border border-gray-200 shadow-sm">
                                        <span className="text-[10px] font-semibold text-blue-500 leading-none uppercase tracking-wide">
                                            {monthAbbr}
                                        </span>
                                        <span className="text-base font-bold text-gray-800 leading-tight">
                                            {dayNumber}
                                        </span>
                                    </div>

                                    {/* Main content */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2">
                                            <span className="text-sm font-medium text-gray-900 leading-snug line-clamp-2 flex-1">
                                                {session.event_name}
                                            </span>
                                            {stateBadge && (
                                                <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium ${stateBadge.className}`}>
                                                    {stateBadge.label}
                                                </span>
                                            )}
                                        </div>

                                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                                            {/* Date + time */}
                                            <span className="flex items-center gap-1 text-[11px] text-gray-500">
                                                <CalendarBlank className="w-3 h-3 shrink-0" />
                                                {dateRangeLabel}
                                                {timeRangeLabel && ` · ${timeRangeLabel}`}
                                            </span>
                                        </div>

                                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                                            {/* Mode badge */}
                                            <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium ${modeBadge.className}`}>
                                                <ModeIcon className="w-3 h-3" />
                                                {modeBadge.label}
                                            </span>

                                            {/* Trainer */}
                                            {session.trainer_name && (
                                                <span className="flex items-center gap-1 text-[11px] text-gray-500">
                                                    <User className="w-3 h-3 shrink-0" />
                                                    {session.trainer_name}
                                                </span>
                                            )}

                                            {/* Registered learners */}
                                            {registeredCount !== null && (
                                                <span className="flex items-center gap-1 text-[11px] text-gray-500">
                                                    <Users className="w-3 h-3 shrink-0" />
                                                    {registeredCount} registered
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
